import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toDisplayScore } from '@cercle/shared';
import { AromaPicker } from '@/shared/reference/AromaPicker';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Kicker,
  Muted,
  ScoreDial,
  Slider,
  Skeleton,
  Textarea,
} from '@/shared/ui';
import { useTastingForm } from './useTastingForm';
import styles from './tasting.module.scss';

/**
 * Écran 04 — saisie de note.
 *
 * L'écriture ne part jamais directement : elle est déposée dans la file offline, qui
 * l'envoie tout de suite s'il y a du réseau et la garde sinon. Le membre n'a donc jamais
 * à se demander s'il est connecté — c'est le principe de l'US-10.
 */
export function TastingScreen(): ReactNode {
  const { id: rumId = '' } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('sessionId') ?? '';
  const navigate = useNavigate();
  const api = useApi();
  const { queueTasting, pendingTasting } = useApp();
  const online = useOnlineStatus();

  const rum = useAsync(() => api.rums.detail(rumId), [rumId]);
  const session = useAsync(
    () => (sessionId ? api.sessions.byId(sessionId) : Promise.resolve(null)),
    [sessionId],
  );
  const existing = useAsync(
    () => (sessionId ? api.tastings.mine(sessionId) : Promise.resolve([])),
    [sessionId],
  );
  const queued = useAsync(() => pendingTasting(sessionId, rumId), [sessionId, rumId]);

  const form = useTastingForm();
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Reprise d'un brouillon déjà déposé pour ce rhum — le membre a pu quitter l'écran.
  // Celui qui attend encore en file passe devant celui du serveur : il est plus récent, et
  // repartir de la version serveur l'écraserait au prochain enregistrement.
  useEffect(() => {
    if (loaded || queued.loading || (!existing.data && !queued.data)) return;
    const own = queued.data ?? existing.data?.find((t) => t.rumId === rumId);
    if (own) {
      form.reset({
        nose: own.nose,
        palate: own.palate,
        finish: own.finish,
        comment: own.comment ?? '',
        aromaTags: own.aromaTags,
      });
    }
    setLoaded(true);
  }, [existing.data, queued.data, queued.loading, form, loaded, rumId]);

  const alreadyValidated = existing.data?.find((t) => t.rumId === rumId)?.validatedAt != null;

  async function submit(validate: boolean): Promise<void> {
    const payload = form.build({ sessionId, rumId, validate });
    if (!payload) return;

    setBusy(true);
    try {
      await queueTasting(payload);
      if (validate) void navigate(`/rhums/${rumId}?sessionId=${sessionId}`);
    } finally {
      setBusy(false);
    }
  }

  if (!sessionId) return <EmptyState>Cette note doit être rattachée à une séance.</EmptyState>;
  if (rum.loading || session.loading) return <Skeleton />;
  if (rum.error || !rum.data) return <EmptyState>{rum.error ?? 'Fiche introuvable.'}</EmptyState>;

  // Garde de dernier recours : on arrive aussi ici par une URL tapée, un retour arrière ou
  // un lien partagé, là où les écrans qui mènent au formulaire ne filtrent plus rien.
  if (session.data && session.data.status !== 'OPEN') {
    return (
      <Card className={styles.locked}>
        <Lock size={18} strokeWidth={2.75} aria-hidden="true" />
        <div>
          <strong>
            {session.data.status === 'CLOSED'
              ? 'La séance est clôturée'
              : 'La séance n’est pas encore lancée'}
          </strong>
          <Muted>
            {session.data.status === 'CLOSED'
              ? 'Les notes sont figées — c’est ce qui les fait entrer dans l’histoire du cercle.'
              : 'Les notes ouvriront dès que la régie lancera la séance.'}
          </Muted>
        </div>
      </Card>
    );
  }

  if (alreadyValidated) {
    return (
      <Card className={styles.locked}>
        <Lock size={18} strokeWidth={2.75} aria-hidden="true" />
        <div>
          <strong>Votre note est validée</strong>
          <Muted>Elle ne peut plus être modifiée — c’est ce qui garantit l’équité du cercle.</Muted>
        </div>
      </Card>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <Kicker>Ma note</Kicker>
          <h1>{rum.data.name}</h1>
          {rum.data.isMasked ? (
            <Muted>Dégustation à l’aveugle</Muted>
          ) : (
            <Muted>
              {[rum.data.country, rum.data.style, rum.data.abv ? `${rum.data.abv} °` : null]
                .filter(Boolean)
                .join(' · ')}
            </Muted>
          )}
        </div>
        <ScoreDial score={form.overall} label="globale" />
      </header>

      <div className={styles.columns}>
        <section className={styles.criteria}>
          <Criterion
            label="Nez"
            value={form.state.nose}
            onChange={(value) => form.setCriterion('nose', value)}
          />
          <Criterion
            label="Bouche"
            value={form.state.palate}
            onChange={(value) => form.setCriterion('palate', value)}
          />
          <Criterion
            label="Finale"
            value={form.state.finish}
            onChange={(value) => form.setCriterion('finish', value)}
          />

          <label className={styles.commentField}>
            <span>Commentaire</span>
            <Textarea
              value={form.state.comment}
              maxLength={1000}
              placeholder="Herbacé, franc, un poivre qui monte…"
              onChange={(event) => form.setComment(event.target.value)}
            />
          </label>
        </section>

        <section className={styles.descriptors}>
          <header className={styles.sectionHead}>
            <span>Descripteurs</span>
          </header>

          {/*
            La roue vit désormais ici : le lien vers l'écran 14 faisait quitter la saisie,
            et le brouillon en cours — curseurs et commentaire — restait dans un état local
            que la navigation emportait.
          */}
          <AromaPicker selected={form.state.aromaTags} onToggle={form.toggleTag} />
        </section>
      </div>

      <footer className={styles.actions}>
        <p className={styles.antiBiasNotice}>
          <Lock size={13} strokeWidth={2.75} aria-hidden="true" />
          Les notes du cercle apparaîtront après validation de la vôtre.
        </p>
        <ErrorText>{form.error}</ErrorText>
        <div className={styles.buttons}>
          <Button variant="secondary" disabled={busy} onClick={() => void submit(false)}>
            Enregistrer le brouillon
          </Button>
          <Button disabled={busy} onClick={() => void submit(true)}>
            Valider ma note
          </Button>
        </div>
        {!online ? (
          <Muted>Hors-ligne : votre note partira automatiquement à la reconnexion.</Muted>
        ) : null}
      </footer>
    </div>
  );
}

/**
 * Curseur d'un critère : rail de 8 px, remplissage cuivre, pouce doré de 30 px et son halo.
 *
 * Le dessin est celui du référentiel, mais l'`input` natif reste dessous : on garde le
 * clavier, le lecteur d'écran et le comportement tactile du navigateur sans les réécrire.
 */
function Criterion({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}): ReactNode {
  return (
    <div className={styles.criterion}>
      <div className={styles.criterionHead}>
        <span>{label}</span>
        <strong>{toDisplayScore(value)}</strong>
      </div>
      <Slider value={value} onChange={onChange} label={`${label} sur 10`} />
    </div>
  );
}
